import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef } from 'react';
import ExtendedSelectField from '~/components/forms/ExtendedSelectField';
import { ToggleRadioField } from '~/components/forms/ToggleRadioField';
import Loader from '~/components/shared/Loader';
import ProviderMatchesSelect from '~/components/Tools/CalculateMaximumMortgage/ProviderMatchesSelect';
import Tooltip from '~/components/ui/Tooltip';
import { formatNumberWithDots } from '~/helpers/utils';
import { useCalculateMaximumMortgageCalculation } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/CalculationContext';
import {
  ENERGY_LABELS,
  EnergyLabelLabels,
  MortgagePeriodMap,
  MortgageResult,
  MortgageTypeLabels,
} from '~/types/Calculations';

export type CalculationResultProps = {
  selectedResult?: MortgageResult;
};

const CalculationResult: React.FC = () => {
  const {
    isCalculating,
    loadedState,
    selected,
    types,
    defaultType,
    periods,
    defaultPeriod,
    nhgEnabled,
    energyLabelsEnabled,
  } = useCalculateMaximumMortgageCalculation();
  const hoverRef = useRef<HTMLButtonElement>(null);

  const factors = [
    nhgEnabled && 'de NHG-instelling',
    energyLabelsEnabled && 'het energielabel',
  ].filter(Boolean);

  return (
    <div className="relative">
      <h2 className="relative z-20 font-display text-2xl text-black">
        Indicatie maximale hypotheek
      </h2>

      {isCalculating && (
        <div className="absolute -inset-x-6 inset-y-0 z-10 flex items-center justify-center rounded-t-lg bg-white/40 backdrop-blur-md md:-inset-x-8 xl:-inset-x-10">
          <Loader showLogo={true} message="Maximale hypotheek wordt berekend" />
        </div>
      )}

      <div className="relative mt-6 space-y-6">
        <h3 className="font-display text-5xl font-bold text-blue-600">
          &euro;{' '}
          {selected?.mortgage?.amount
            ? formatNumberWithDots(selected?.mortgage.amount)
            : '123.456'}
        </h3>

        <div className="grid grid-cols-2 gap-y-2 font-display">
          <div>Bruto maandbedrag</div>
          <div className="font-bold">
            &euro;{' '}
            {selected?.mortgage?.grossMonthlyCosts
              ? formatNumberWithDots(selected?.mortgage.grossMonthlyCosts, 2, 2)
              : '789,00'}
          </div>

          <div>Netto maandbedrag</div>
          <div className="font-bold">
            &euro;{' '}
            {selected?.mortgage?.netMonthlyCosts
              ? formatNumberWithDots(selected?.mortgage.netMonthlyCosts, 2, 2)
              : '654,00'}
          </div>

          <div>Rentepercentage</div>
          <div className="flex items-center gap-2 font-bold">
            <span>{selected?.mortgage?.rate || '4'} %</span>
            <div>
              <button
                ref={hoverRef}
                className="text-blue-600 hover:text-blue-800"
                type="button"
              >
                <FontAwesomeIcon icon={faCircleInfo} />
              </button>
              <Tooltip hoverRef={hoverRef}>
                {factors.length > 0 ? (
                  <>
                    De berekening en het rentepercentage is gebaseerd op{' '}
                    {factors.join(' en ')} die je hieronder hebt gekozen. Laat
                    je hierover goed adviseren.
                  </>
                ) : (
                  <>
                    Bij de berekening en het rentepercentage is geen rekening
                    gehouden met Nationale Hypotheek Garantie (NHG). Laat je
                    hierover goed adviseren.
                  </>
                )}
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="border-b-2 border-stone-50 pb-2">
          <p className="text-sm italic text-stone-300">
            Dit is een indicatie, voor een nauwkeurige berekening voor jouw
            situatie adviseren we om een goede financieel professional te
            benaderen.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <ProviderMatchesSelect />
          <ExtendedSelectField
            name="mortgageType"
            label="Hypotheekvorm"
            options={types.map((type) => ({
              label: MortgageTypeLabels[type],
              value: type,
              selected: defaultType == type,
              isLoading: !loadedState.some((s) => s.type === type),
            }))}
          />
          <ExtendedSelectField
            name="interestPeriod"
            label="Rentevastperiode"
            options={periods
              .sort((a, b) => a - b)
              .map((period) => ({
                label: `${period} jaar`,
                value: MortgagePeriodMap[period],
                selected: defaultPeriod == period,
                isLoading: !loadedState.some(
                  (s) =>
                    s.type === selected?.mortgage.type &&
                    s.periods.includes(period)
                ),
              }))}
          />
          {nhgEnabled && (
            <div className="col-span-2 sm:col-span-1">
              <ToggleRadioField
                name="useNhg"
                label="NHG"
                gridClassName="grid grid-cols-2 gap-3"
                labelClassName="h-[44px] md:h-[46px] w-full"
                options={[
                  { value: true, label: 'Ja' },
                  { value: false, label: 'Nee' },
                ]}
              />
            </div>
          )}
          {energyLabelsEnabled && (
            <div className="col-span-2 sm:col-span-1">
              <ExtendedSelectField
                name="energyLabel"
                label="Energielabel"
                options={ENERGY_LABELS.map((label) => ({
                  label: EnergyLabelLabels[label],
                  value: label,
                  selected: label === 'Unknown',
                }))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalculationResult;
